import cx from 'classnames';
import { useNavigate } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/20/solid';
import times from 'lodash/times';
import { useState } from 'react';

import { Report } from 'graphql-api';
import {
  NUMBER_OF_DATA_IMPORT_STEP,
  DATA_IMPORT_STEPS_MAPPING,
  REVIEW_DATA_STEP
} from 'constants/report-steps';
import { navigateAndStorePreviousStep } from 'utils/navigation';
import ConfirmationModal from 'components/confirmation-modal';

function StepProgressBar({
  report,
  currentStep,
  rightElement
}: {
  report: Report;
  currentStep: string;
  rightElement?: JSX.Element;
}): JSX.Element {
  const navigate = useNavigate();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const importStepIndex = DATA_IMPORT_STEPS_MAPPING[currentStep];

  const steps = times(NUMBER_OF_DATA_IMPORT_STEP, (step) => {
    const status =
      step + 1 === importStepIndex
        ? 'current'
        : step + 1 < importStepIndex
        ? 'complete'
        : 'upcoming';
    return { status };
  });

  const handleGoBackClick = (): void => {
    if (currentStep === REVIEW_DATA_STEP) {
      setIsConfirmationModalOpen(true);
    } else {
      void navigateAndStorePreviousStep(report, currentStep, navigate);
    }
  };

  return (
    <div className="flex mb-14 items-center">
      <div className="flex flex-1">
        <a onClick={handleGoBackClick} className="btn-secondary cursor-pointer">
          Back to step {importStepIndex - 1}
        </a>
        <ConfirmationModal
          open={isConfirmationModalOpen}
          setOpen={setIsConfirmationModalOpen}
          handleConfirmationClick={() => {
            void navigateAndStorePreviousStep(report, currentStep, navigate);
          }}
          title="Are you sure you want to start over?"
          content={
            "You will lose any progress you have made up to this point. You'll need to reconnect your payroll provider/HRIS \
            or reupload your data to continue from step 1. Hint: Export your data first if you want to save a copy of your \
            progress at this step."
          }
        />
      </div>
      <nav aria-label="Progress">
        <ol role="list" className="flex items-center">
          {steps.map((step, stepIdx) => (
            <li
              key={stepIdx}
              className={cx('relative', { 'pr-8 sm:pr-20': stepIdx !== steps.length - 1 })}>
              {step.status === 'complete' ? (
                <>
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="h-0.5 w-full bg-blue-500" />
                  </div>
                  <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-blue-500">
                    <CheckIcon className="h-5 w-5 text-white" aria-hidden="true" />
                  </div>
                </>
              ) : step.status === 'current' ? (
                <>
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="h-0.5 w-full bg-gray-200" />
                  </div>
                  <div
                    className="relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-blue-500 bg-white"
                    aria-current="step">
                    <span className="h-2.5 w-2.5 rounded-full bg-blue-500" aria-hidden="true" />
                  </div>
                </>
              ) : (
                <>
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="h-0.5 w-full bg-gray-200" />
                  </div>
                  <div className="group relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white"></div>
                </>
              )}
            </li>
          ))}
        </ol>
      </nav>
      <div className="flex flex-1 justify-end">{rightElement != null && rightElement}</div>
    </div>
  );
}

export default StepProgressBar;
